import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { API, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSeo } from "@/lib/seo";

const COLORS = ["#CCFF00", "#FF007A", "#00F0FF", "#8B00FF", "#fff"];

const Confetti = () => (
  <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
    {Array.from({ length: 40 }).map((_, i) => (
      <motion.span key={i} className="absolute top-0 h-3 w-2 rounded-sm"
        style={{ left: `${(i * 37) % 100}%`, background: COLORS[i % COLORS.length] }}
        initial={{ y: -40, rotate: 0, opacity: 1 }}
        animate={{ y: "105vh", rotate: 360 + i * 20, opacity: [1, 1, 0] }}
        transition={{ duration: 2.4 + (i % 5) * 0.4, delay: (i % 10) * 0.08, ease: "easeIn" }} />
    ))}
  </div>
);

const STATES = {
  checking: { title: "Confirming...", text: "Hold tight, we're checking with the bank." },
  paid: { title: "You're in.", text: "Payment confirmed. Your feet are about to get loud." },
  failed: { title: "Payment failed", text: "Something went wrong or the session expired. Your bag is still saved." },
  timeout: { title: "Still processing", text: "We couldn't confirm yet. Check your email for a receipt shortly." },
};

export default function PaymentSuccess() {
  useSeo({ title: "Order status | Insane Circle", description: "Your Insane Circle order status.", path: "/payment/success", noindex: true });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(sessionId ? "checking" : "failed");
  const [amount, setAmount] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") {
          setAmount(data?.amount ?? null);
          setState("paid");
          clear();
          return;
        }
        if (data?.status === "expired") { setState("failed"); return; }
      } catch {
        if (tries > 3) { setState("failed"); return; }
      }
      if (tries >= 10) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const s = STATES[state];
  return (
    <section data-testid="payment-success-page" className="flex min-h-screen items-center px-5 pt-24 md:px-10">
      {state === "paid" && <Confetti />}
      <div className="mx-auto max-w-3xl text-center">
        {state === "checking" && <Loader2 className="mx-auto mb-6 h-10 w-10 animate-spin text-lime" />}
        <motion.h1 key={state} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} data-testid="payment-status-title"
          className={`font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl ${state === "paid" ? "text-lime" : ""}`}>
          {s.title}
        </motion.h1>
        <p data-testid="payment-status-text" className="mx-auto mt-6 max-w-md text-base text-haze md:text-lg">{s.text}</p>
        {amount !== null && <p data-testid="payment-amount" className="mt-4 font-mono text-lg">Paid {money(amount)}</p>}
        <Link to="/" data-testid="payment-back-home" className="mt-10 inline-block rounded-full bg-lime px-8 py-4 font-display text-lg font-extrabold uppercase text-ink transition-transform hover:scale-105">
          Back to the drop
        </Link>
      </div>
    </section>
  );
}
